import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produto não encontrado');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Faça login para adicionar ao carrinho');
      navigate('/auth');
      return;
    }
    try {
      await addToCart(product.id, quantity);
      toast.success('Produto adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    }
  };

  const handleToggleWishlist = async () => {
    if (!user) {
      toast.error('Faça login para adicionar aos favoritos');
      navigate('/auth');
      return;
    }
    try {
      await axios.post(`${API}/wishlist/${product.id}`, {}, { withCredentials: true });
      toast.success('Adicionado aos favoritos!');
    } catch (error) {
      toast.error('Erro ao adicionar aos favoritos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!product) return null;

  const avgRating = typeof product?.rating_avg === 'number'
    ? product.rating_avg
    : (reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0);

  const ratingCount = typeof product?.rating_count === 'number'
    ? product.rating_count
    : (Array.isArray(reviews) ? reviews.length : 0);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 mb-12">
          {/* Images */}
          <div>
            <div className="aspect-square bg-white dark:bg-slate-800 rounded-2xl overflow-hidden mb-4">
              <img
                src={product.images[selectedImage] || 'https://via.placeholder.com/600'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 ${
                      selectedImage === idx ? 'border-blue-600' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4" data-testid="product-name">{product.name}</h1>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(avgRating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-slate-600 dark:text-slate-400">({ratingCount} avaliações)</span>
            </div>
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-6" data-testid="product-price">
              R$ {product.price.toFixed(2)}
            </p>
            <p className="text-slate-600 dark:text-slate-400 mb-6" data-testid="product-description">{product.description}</p>
            
            {product.stock > 0 ? (
              <p className="text-green-600 dark:text-green-400 mb-6">Em estoque: {product.stock} unidades</p>
            ) : (
              <p className="text-red-600 dark:text-red-400 mb-6">Produto esgotado</p>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity"
                >
                  -
                </Button>
                <span className="w-12 text-center font-semibold" data-testid="quantity">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={quantity >= product.stock}
                  data-testid="increase-quantity"
                >
                  +
                </Button>
              </div>
              <Button
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                className="flex-1"
                size="lg"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Adicionar ao Carrinho
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleToggleWishlist}
                data-testid="add-to-wishlist-btn"
              >
                <Heart className="h-5 w-5" />
              </Button>
            </div>

            {/* Specifications */}
            {Object.keys(product.specifications).length > 0 && (
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl mb-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Especificações</h3>
                <dl className="space-y-2">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
                      <dt className="text-slate-600 dark:text-slate-400">{key}</dt>
                      <dd className="font-semibold text-slate-900 dark:text-white">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">Avaliações</h2>
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.slice(0, 10).map(review => {
                const displayName = review.user_name || (review.comment?.split(' - ').pop()) || 'Cliente';
                const displayComment = review.user_name ? review.comment : (review.comment?.split(' - ')[0] || review.comment);
                return (
                  <div key={review.id} className="border-b border-slate-200 dark:border-slate-700 pb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-900 dark:text-white">{displayName}</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400">{displayComment}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-600 dark:text-slate-400">Nenhuma avaliação ainda.</p>
          )}
        </div>
      </div>
    </div>
  );
};